// Keep the requested output budget small enough for provider-side request validation.
const MAX_COMPLETION_TOKENS = 1024;
const MAX_HISTORY_CHARACTERS = 1200;
const MAX_CONTINUATION_CHARACTERS = 1200;
const MAX_COMPACT_CHARACTERS = 250;
const MAX_EMERGENCY_COMPACT_CHARACTERS = 100;
const MAX_REQUEST_BODY_BYTES = 2048;
const MAX_RESPONSE_CONTINUATIONS = 1;
const CONTINUATION_PROMPT =
    'Continue the response exactly where it stopped. Do not repeat any text. Output only the continuation.';
const CONTENT_TRUNCATION_MARKER = '\n\n[Earlier content trimmed to fit the request size.]\n\n';

function clipContent(content, maxCharacters) {
    if (content.length <= maxCharacters) {
        return content;
    }

    if (maxCharacters <= 0) {
        return '';
    }

    if (maxCharacters <= CONTENT_TRUNCATION_MARKER.length) {
        return content.slice(0, maxCharacters);
    }

    const availableCharacters = maxCharacters - CONTENT_TRUNCATION_MARKER.length;
    const headCharacters = Math.ceil(availableCharacters / 2);
    const tailCharacters = availableCharacters - headCharacters;
    return `${content.slice(0, headCharacters)}${CONTENT_TRUNCATION_MARKER}${content.slice(-tailCharacters)}`;
}

function getUtf8ByteLength(value) {
    if (typeof TextEncoder === 'function') {
        return new TextEncoder().encode(value).length;
    }
    return value.length;
}

function selectRecentHistory(history, maxCharacters = MAX_HISTORY_CHARACTERS) {
    const recentHistory = [];
    let historyCharacters = 0;

    for (let index = history.length - 1; index >= 0; index--) {
        const message = history[index];
        if (
            !message ||
            !['user', 'assistant'].includes(message.role) ||
            typeof message.content !== 'string'
        ) {
            continue;
        }

        const remainingCharacters = maxCharacters - historyCharacters;
        if (remainingCharacters <= 0) {
            break;
        }

        if (recentHistory.length > 0 && message.content.length > remainingCharacters) {
            break;
        }

        const content = clipContent(message.content, remainingCharacters);
        recentHistory.unshift({
            role: message.role,
            content,
        });

        historyCharacters += content.length;
        if (content.length < message.content.length) {
            break;
        }
    }

    return recentHistory;
}

function buildConversationMessages(systemPrompt, history, maxCharacters = MAX_HISTORY_CHARACTERS) {
    return [
        { role: 'system', content: systemPrompt },
        ...selectRecentHistory(history, maxCharacters),
    ];
}

function buildCompactConversationMessages(
    messages,
    maxCharacters = MAX_COMPACT_CHARACTERS,
) {
    const systemMessage = messages.find((message) => message?.role === 'system');
    const nonSystemMessages = messages.filter((message) => message?.role !== 'system');
    const continuationMessage = nonSystemMessages.at(-1);
    const isContinuation =
        continuationMessage?.role === 'user' &&
        continuationMessage.content === CONTINUATION_PROMPT;
    const latestUserMessage = isContinuation
        ? [...nonSystemMessages]
              .reverse()
              .find((message) => message?.role === 'user' && message !== continuationMessage)
        : [...nonSystemMessages]
              .reverse()
              .find((message) => message?.role === 'user');

    const compactMessages = systemMessage
        ? [{ role: 'system', content: systemMessage.content }]
        : [];

    if (!isContinuation) {
        if (latestUserMessage) {
            compactMessages.push({
                role: 'user',
                content: clipContent(latestUserMessage.content, maxCharacters),
            });
        }
        return compactMessages;
    }

    const latestAssistantMessage = [...nonSystemMessages]
        .reverse()
        .find((message) => message?.role === 'assistant');
    const availableCharacters = Math.max(0, maxCharacters - CONTINUATION_PROMPT.length);
    const userCharacters = Math.floor(availableCharacters / 2);
    const assistantCharacters = availableCharacters - userCharacters;

    if (latestUserMessage) {
        compactMessages.push({
            role: 'user',
            content: clipContent(latestUserMessage.content, userCharacters),
        });
    }
    if (latestAssistantMessage) {
        compactMessages.push({
            role: 'assistant',
            content: clipContent(latestAssistantMessage.content, assistantCharacters),
        });
    }
    compactMessages.push({ role: 'user', content: CONTINUATION_PROMPT });
    return compactMessages;
}

function fitRequestToBodyLimit(messages, serializeRequest) {
    let requestMessages = messages;
    let requestBody = serializeRequest(requestMessages);
    const characterLimits = [MAX_COMPACT_CHARACTERS, MAX_EMERGENCY_COMPACT_CHARACTERS, 0];

    if (getUtf8ByteLength(requestBody) <= MAX_REQUEST_BODY_BYTES) {
        return { messages: requestMessages, body: requestBody, fits: true };
    }

    for (const maxCharacters of characterLimits) {
        requestMessages = buildCompactConversationMessages(messages, maxCharacters);
        requestBody = serializeRequest(requestMessages);
        if (getUtf8ByteLength(requestBody) <= MAX_REQUEST_BODY_BYTES) {
            return { messages: requestMessages, body: requestBody, fits: true };
        }
    }

    return {
        messages: requestMessages,
        body: requestBody,
        fits: getUtf8ByteLength(requestBody) <= MAX_REQUEST_BODY_BYTES,
    };
}

function buildContinuationMessages(
    messages,
    answer,
    maxCharacters = MAX_CONTINUATION_CHARACTERS,
) {
    const systemMessage = messages.find((message) => message?.role === 'system');
    const history = messages.filter((message) => message?.role !== 'system');
    const continuationAnswer =
        typeof answer === 'string' ? clipContent(answer, maxCharacters) : '';
    const historyCharacters = Math.max(0, maxCharacters - continuationAnswer.length);

    return [
        ...(systemMessage
            ? [{ role: 'system', content: systemMessage.content }]
            : []),
        ...selectRecentHistory(history, historyCharacters),
        ...(continuationAnswer
            ? [{ role: 'assistant', content: continuationAnswer }]
            : []),
        { role: 'user', content: CONTINUATION_PROMPT },
    ];
}

function getResponseParts(message) {
    let answer = typeof message?.content === 'string' ? message.content : '';
    let reasoning = typeof message?.reasoning === 'string' ? message.reasoning : '';

    if (!reasoning && typeof message?.reasoning_content === 'string') {
        reasoning = message.reasoning_content;
    }

    const thinkBlock = /<think(?:ing)?\b[^>]*>([\s\S]*?)<\/think(?:ing)?\s*>/gi;
    answer = answer.replace(thinkBlock, function (match, thought) {
        const trimmedThought = thought.trim();
        if (trimmedThought) {
            reasoning = reasoning ? `${reasoning}\n\n${trimmedThought}` : trimmedThought;
        }
        return '';
    }).trim();

    return { answer, reasoning: reasoning.trim() };
}

function mergeResponseParts(current, next) {
    const reasoning = [current.reasoning, next.reasoning].filter(Boolean).join('\n\n');
    const separator =
        current.answer &&
        next.answer &&
        !/\s$/.test(current.answer) &&
        !/^\s/.test(next.answer)
            ? '\n'
            : '';
    return {
        answer: `${current.answer || ''}${separator}${next.answer || ''}`,
        reasoning,
    };
}

module.exports = {
    CONTINUATION_PROMPT,
    MAX_COMPLETION_TOKENS,
    MAX_CONTINUATION_CHARACTERS,
    MAX_EMERGENCY_COMPACT_CHARACTERS,
    MAX_HISTORY_CHARACTERS,
    MAX_COMPACT_CHARACTERS,
    MAX_REQUEST_BODY_BYTES,
    MAX_RESPONSE_CONTINUATIONS,
    buildContinuationMessages,
    buildCompactConversationMessages,
    buildConversationMessages,
    fitRequestToBodyLimit,
    getResponseParts,
    getUtf8ByteLength,
    mergeResponseParts,
    selectRecentHistory,
};
